// Roles and permissions (spec §2, plan §4 and §6). ROLE_PERMISSIONS is the single matrix that the
// API's requirePermission() and the web menu both read.

export const ROLES = ['owner', 'supervisor', 'helper', 'lead', 'attester'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'task.read',
  'task.transition',
  'task.edit',
  'task.assign',
  'task.replan',
  'task.verify',
  'step.tick',
  'comment.read',
  'comment.write',
  'evidence.read',
  'evidence.write',
  'evidence.check',
  'checklist.read',
  'checklist.run',
  'checklist.sign',
  'people.read',
  'leads.read',
  'leads.edit',
  'leads.decide',
  'start.read',
  'start.take',
  'induction.manage',
  'content.read',
  'history.read',
  'freeze.create',
  'freeze.read',
  'pack.manage',
  'import.tracker',
  'export.team',
  'export.full',
  'user.manage',
  'purge',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

// Every program member takes the induction (spec P10), so these are in every role.
const START: readonly Permission[] = ['start.read', 'start.take'];

const TEAM_READ: readonly Permission[] = [
  'task.read',
  'comment.read',
  'evidence.read',
  'checklist.read',
  'people.read',
  'content.read',
  'history.read',
  'freeze.read',
];

// Owner-only powers (spec §2): content packs, users, Leads edits, purge, freeze, induction admin.
export const OWNER_ONLY: readonly Permission[] = [
  'leads.edit',
  'induction.manage',
  'freeze.create',
  'pack.manage',
  'import.tracker',
  'user.manage',
  'purge',
];

const SUPERVISOR: readonly Permission[] = [
  ...START,
  ...TEAM_READ,
  'task.transition',
  'task.edit',
  'task.assign',
  'task.replan',
  'task.verify',
  'step.tick',
  'comment.write',
  'evidence.write',
  'evidence.check',
  'checklist.run',
  'checklist.sign',
  'leads.read',
  'export.team',
];

const HELPER: readonly Permission[] = [
  ...START,
  ...TEAM_READ,
  'task.transition',
  'task.edit',
  'step.tick',
  'comment.write',
  'evidence.write',
  'checklist.run',
  'export.team',
];

// Leads read everything and decide asks and acceptances; they do not edit tasks (spec §2).
const LEAD: readonly Permission[] = [
  ...START,
  ...TEAM_READ,
  'leads.read',
  'leads.decide',
  'export.full',
];

// Attesters see only the checklists assigned to them and Start here (spec §2, P9). The supervisor
// keeps checklist.sign for co-supervision, but the checklist scope check (assertMaySign) lets a
// co-supervisor sign only a checklist that has no attester role (security.md B-R13).
const ATTESTER: readonly Permission[] = [...START, 'checklist.read', 'checklist.sign'];

export const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> = Object.freeze({
  owner: new Set<Permission>(PERMISSIONS),
  supervisor: new Set<Permission>(SUPERVISOR),
  helper: new Set<Permission>(HELPER),
  lead: new Set<Permission>(LEAD),
  attester: new Set<Permission>(ATTESTER),
});

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

export function permissionsOf(role: Role): Permission[] {
  return PERMISSIONS.filter((p) => ROLE_PERMISSIONS[role].has(p));
}

// Roles that may see Leads data (constitution §6.4). Helpers and attesters never do.
export const LEADS_ROLES: readonly Role[] = ['owner', 'supervisor', 'lead'];

export function isLeadsRole(role: Role): boolean {
  return LEADS_ROLES.includes(role);
}

// Supervision is co-owned by the owner and the supervisor (spec P5).
export function isCoSupervisor(role: Role): boolean {
  return role === 'owner' || role === 'supervisor';
}

// Entra app role that must be present in the token for each portal role (spec §2).
export function entraAppRoleFor(role: Role): 'CIS.Leads' | 'CIS.Team' {
  return isLeadsRole(role) ? 'CIS.Leads' : 'CIS.Team';
}
